import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { PaginationMeta } from '@/modules/auth/types';

export const SUPPORT_ROUTES = {
  TICKETS: '/suporte',
  SAAS_TICKETS: '/saas/suporte',
} as const;

const ENDPOINT = '/api/v1/support/tickets';
const KEY = ['support', 'tickets'];

export type TicketStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED';

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em atendimento',
  RESOLVED: 'Resolvido',
};

export interface TicketMessage {
  id: string;
  authorName: string;
  fromSaas: boolean;
  body: string;
  images: string[];
  createdAt: string;
}

export interface Ticket {
  id: string;
  companyId: string;
  companyName: string;
  subject: string;
  status: TicketStatus;
  messagesTotal: number;
  lastMessageAt: string | null;
  createdAt: string;
}

export interface TicketDetail extends Ticket {
  messages: TicketMessage[];
}

export interface TicketsFilter {
  status?: TicketStatus | '';
  search?: string;
  page?: number;
}

export function useTickets(filter: TicketsFilter = {}) {
  const busca = new URLSearchParams({
    ...(filter.status ? { status: filter.status } : {}),
    ...(filter.search ? { search: filter.search } : {}),
    page: String(filter.page ?? 1),
  }).toString();

  return useQuery({
    queryKey: [...KEY, busca],
    queryFn: () => apiClient.get<{ data: Ticket[]; meta: PaginationMeta }>(`${ENDPOINT}?${busca}`),
  });
}

export function useTicket(id: string | null) {
  return useQuery({
    queryKey: [...KEY, id],
    enabled: Boolean(id),
    queryFn: () => apiClient.get<TicketDetail>(`${ENDPOINT}/${id}`),
  });
}

function useTicketMutation<TArgs>(mutationFn: (args: TArgs) => Promise<TicketDetail>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCreateTicket() {
  return useTicketMutation((input: { subject: string; body: string; images: string[] }) =>
    apiClient.post<TicketDetail>(ENDPOINT, input),
  );
}

export function useReplyTicket() {
  return useTicketMutation(({ id, ...input }: { id: string; body: string; images: string[] }) =>
    apiClient.post<TicketDetail>(`${ENDPOINT}/${id}/messages`, input),
  );
}

export function useUpdateTicket() {
  return useTicketMutation(({ id, status }: { id: string; status: TicketStatus }) =>
    apiClient.patch<TicketDetail>(`${ENDPOINT}/${id}`, { status }),
  );
}
